import React, { useState, useEffect } from "react";
import useAxios from "../../utils/useAxios";

const DeliveryGigs = () => {
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [editingGig, setEditingGig] = useState(null);
  const [formData, setFormData] = useState({
    gigCode: "",
    title: "",
    timeFrom: "15:00",
    timeTo: "19:00",
    timeRange: "03:00 PM - 07:00 PM",
    hours: "4.0",
    estPayoutMin: "300",
    estPayoutMax: "500",
  });

  const axiosAPI = useAxios();

  const convert24to12 = (timeStr) => {
    if (!timeStr) return "";
    if (timeStr.includes("AM") || timeStr.includes("PM")) return timeStr;
    const parts = timeStr.split(":");
    if (parts.length < 2) return timeStr;
    let hours = parseInt(parts[0], 10);
    const minutes = parts[1];
    if (isNaN(hours)) return timeStr;
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    const hoursFormatted = hours < 10 ? `0${hours}` : hours;
    return `${hoursFormatted}:${minutes} ${ampm}`;
  };

  const convert12to24 = (time12Str) => {
    if (!time12Str) return "";
    const match = time12Str.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return "";
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const ampm = match[3].toUpperCase();
    if (ampm === "PM" && hours < 12) hours += 12;
    if (ampm === "AM" && hours === 12) hours = 0;
    const hStr = hours < 10 ? `0${hours}` : `${hours}`;
    return `${hStr}:${minutes}`;
  };

  const handleTimeChange = (field, value) => {
    const fromStr = field === "timeFrom" ? value : formData.timeFrom;
    const toStr = field === "timeTo" ? value : formData.timeTo;

    const formattedFrom = convert24to12(fromStr);
    const formattedTo = convert24to12(toStr);

    let calculatedHours = formData.hours;
    if (fromStr && toStr && fromStr.includes(":") && toStr.includes(":")) {
      const [h1, m1] = fromStr.split(":").map(Number);
      const [h2, m2] = toStr.split(":").map(Number);
      let startMin = h1 * 60 + m1;
      let endMin = h2 * 60 + m2;
      if (endMin < startMin) endMin += 24 * 60;
      const diffHours = (endMin - startMin) / 60;
      if (diffHours > 0) {
        calculatedHours = String(diffHours.toFixed(1));
      }
    }

    const newTimeRange =
      formattedFrom && formattedTo ? `${formattedFrom} - ${formattedTo}` : formData.timeRange;

    setFormData((prev) => ({
      ...prev,
      [field]: value,
      timeRange: newTimeRange,
      hours: calculatedHours,
    }));
  };

  const fetchGigs = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosAPI.get("/api/delivery-partner/gigs");
      if (res.data && res.data.success) {
        setGigs(res.data.gigs || []);
      } else {
        setError(res.data?.message || "Failed to fetch gigs");
      }
    } catch (err) {
      console.error("fetchGigs error:", err);
      setError("Failed to connect to backend server");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGigs();
  }, []);

  const handleOpenAddModal = () => {
    setEditingGig(null);
    setFormData({
      gigCode: `gig-${Date.now().toString().slice(-4)}`,
      title: "",
      timeFrom: "15:00",
      timeTo: "19:00",
      timeRange: "03:00 PM - 07:00 PM",
      hours: "4.0",
      estPayoutMin: "300",
      estPayoutMax: "500",
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (gig) => {
    setEditingGig(gig);
    const timeSlot = gig.timeRange || "";
    let from24 = "";
    let to24 = "";
    if (timeSlot.includes("-")) {
      const parts = timeSlot.split("-").map((s) => s.trim());
      from24 = convert12to24(parts[0]) || "";
      to24 = convert12to24(parts[1]) || "";
    }
    setFormData({
      gigCode: gig.id || gig.gigCode || "",
      title: gig.title || "",
      timeFrom: from24,
      timeTo: to24,
      timeRange: timeSlot,
      hours: String(gig.hours || "4.0"),
      estPayoutMin: String(gig.estPayoutMin || "300"),
      estPayoutMax: String(gig.estPayoutMax || "500"),
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingGig(null);
  };

  const handleSaveGig = async (e) => {
    e.preventDefault();
    const formattedFrom = convert24to12(formData.timeFrom);
    const formattedTo = convert24to12(formData.timeTo);
    const computedTimeRange =
      formData.timeRange ||
      (formattedFrom && formattedTo ? `${formattedFrom} - ${formattedTo}` : "");

    if (!formData.title.trim() || !computedTimeRange.trim()) {
      if (window.notifyMessage) {
        window.notifyMessage(
          "Warning",
          "Please fill in Shift Title, Start Time, and End Time.",
          "warning",
        );
      } else {
        alert("Please fill in Shift Title, Start Time, and End Time.");
      }
      return;
    }

    const payload = {
      ...formData,
      timeRange: computedTimeRange,
    };

    setSaving(true);
    setError(null);
    setSuccessMsg(null);

    try {
      if (editingGig) {
        // Update existing gig
        const gigId = editingGig.dbId || editingGig.id;
        const res = await axiosAPI.put(
          `/api/delivery-partner/gigs/${gigId}`,
          payload,
        );
        if (res.data && res.data.success) {
          if (window.notifyMessage) {
            window.notifyMessage(
              "Success",
              "Delivery Shift Gig updated successfully!",
            );
          } else {
            setSuccessMsg("Delivery Shift Gig updated successfully!");
          }
          fetchGigs();
          handleCloseModal();
        } else {
          setError(res.data?.message || "Failed to update gig");
        }
      } else {
        // Create new gig
        const res = await axiosAPI.post("/api/delivery-partner/gigs", payload);
        if (res.data && res.data.success) {
          if (window.notifyMessage) {
            window.notifyMessage(
              "Success",
              "New Delivery Shift Gig created successfully!",
            );
          } else {
            setSuccessMsg("New Delivery Shift Gig created successfully!");
          }
          fetchGigs();
          handleCloseModal();
        } else {
          setError(res.data?.message || "Failed to create gig");
        }
      }
    } catch (err) {
      console.error("Save gig error:", err);
      const msg = err.response?.data?.message || "Error saving gig shift";
      if (window.notifyMessage) {
        window.notifyMessage("Error", msg, "danger");
      } else {
        setError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGig = async (gig) => {
    const gigId = gig.dbId || gig.id;
    if (!window.confirm(`Are you sure you want to delete "${gig.title}"?`)) {
      return;
    }

    try {
      const res = await axiosAPI.delete(`/api/delivery-partner/gigs/${gigId}`);
      if (res.data && res.data.success) {
        if (window.notifyMessage) {
          window.notifyMessage(
            "Success",
            "Delivery Shift Gig deleted successfully!",
          );
        } else {
          setSuccessMsg("Delivery Shift Gig deleted successfully!");
        }
        fetchGigs();
      } else {
        if (window.notifyMessage) {
          window.notifyMessage(
            "Error",
            res.data?.message || "Failed to delete gig",
            "danger",
          );
        } else {
          setError(res.data?.message || "Failed to delete gig");
        }
      }
    } catch (err) {
      console.error("Delete gig error:", err);
      if (window.notifyMessage) {
        window.notifyMessage("Error", "Error deleting gig shift", "danger");
      } else {
        setError("Error deleting gig shift");
      }
    }
  };

  return (
    <div
      className="container"
      style={{ paddingTop: "20px", paddingBottom: "40px" }}
    >
      <div className="page-inner">
        {/* Page Header */}
        <div className="d-flex flex-column align-items-start mb-4">
          <h3 className="fw-bold mb-1" style={{ color: "#FF6D1F" }}>
            <i className="fas fa-clock me-2"></i> Delivery Partner Shift Gigs
          </h3>
          <p className="text-muted mb-0">
            Create, edit, and configure available work shift slots for delivery
            partners dynamically.
          </p>
        </div>

        {/* Notification Alerts */}
        {successMsg && (
          <div
            className="alert alert-success alert-dismissible fade show"
            role="alert"
          >
            <i className="fas fa-check-circle me-2"></i> {successMsg}
            <button
              type="button"
              className="btn-close"
              onClick={() => setSuccessMsg(null)}
            ></button>
          </div>
        )}
        {error && (
          <div
            className="alert alert-danger alert-dismissible fade show"
            role="alert"
          >
            <i className="fas fa-exclamation-triangle me-2"></i> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
            ></button>
          </div>
        )}

        {/* Summary Stats Cards */}
        <div className="row mb-4">
          <div className="col-sm-6 col-md-4">
            <div className="card card-stats card-round">
              <div className="card-body">
                <div className="row align-items-center">
                  <div className="col-icon">
                    <div className="icon-big text-center icon-primary bubble-shadow-small">
                      <i className="fas fa-clock"></i>
                    </div>
                  </div>
                  <div className="col col-stats ms-3 ms-sm-0">
                    <div className="numbers">
                      <p className="card-category">Active Shift Gigs</p>
                      <h4 className="card-title">{gigs.length} Slots</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-sm-6 col-md-4">
            <div className="card card-stats card-round">
              <div className="card-body">
                <div className="row align-items-center">
                  <div className="col-icon">
                    <div className="icon-big text-center icon-success bubble-shadow-small">
                      <i className="fas fa-coins"></i>
                    </div>
                  </div>
                  <div className="col col-stats ms-3 ms-sm-0">
                    <div className="numbers">
                      <p className="card-category">Est. Peak Payout</p>
                      <h4 className="card-title">₹300 - ₹600</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-sm-6 col-md-4">
            <div className="card card-stats card-round">
              <div className="card-body">
                <div className="row align-items-center">
                  <div className="col-icon">
                    <div className="icon-big text-center icon-secondary bubble-shadow-small">
                      <i className="fas fa-motorcycle"></i>
                    </div>
                  </div>
                  <div className="col col-stats ms-3 ms-sm-0">
                    <div className="numbers">
                      <p className="card-category">Delivery App Status</p>
                      <h4 className="card-title">Live & Synced</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Gigs Table List */}
        <div className="card card-round">
          <div className="card-header d-flex align-items-center justify-content-between">
            <div className="card-title">
              <i className="fas fa-list me-2 text-primary"></i> Available Shift
              Slots
            </div>
            <div>
              <button
                className="btn btn-primary btn-round me-2"
                onClick={handleOpenAddModal}
                style={{
                  backgroundColor: "#FF6D1F",
                  borderColor: "#FF6D1F",
                  fontWeight: "600",
                }}
              >
                <i className="fa fa-plus me-1"></i> Add New Shift Gig
              </button>
              <button
                className="btn btn-outline-secondary btn-round"
                onClick={fetchGigs}
                disabled={loading}
              >
                <i
                  className={`fas fa-sync ${loading ? "fa-spin" : ""} me-1`}
                ></i>{" "}
                Refresh
              </button>
            </div>
          </div>

          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted fw-semibold">
                  Loading gig shifts from server...
                </p>
              </div>
            ) : gigs.length === 0 ? (
              <div className="text-center py-5">
                <p className="text-muted fs-5 mb-3">
                  No delivery gig shifts created yet.
                </p>
                <button
                  className="btn btn-primary btn-round"
                  onClick={handleOpenAddModal}
                  style={{ backgroundColor: "#FF6D1F", borderColor: "#FF6D1F" }}
                >
                  + Create First Gig Shift
                </button>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table align-items-center mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th scope="col">Code</th>
                      <th scope="col">Shift Title</th>
                      <th scope="col">Time Slot</th>
                      <th scope="col">Duration</th>
                      <th scope="col">Est. Payout Range</th>
                      <th scope="col" className="text-end">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {gigs.map((gig) => (
                      <tr key={gig.dbId || gig.id}>
                        <td>
                          <span className="badge badge-secondary font-monospace">
                            {gig.id || gig.gigCode}
                          </span>
                        </td>
                        <td>
                          <div className="fw-bold text-dark">{gig.title}</div>
                        </td>
                        <td>
                          <span className="badge badge-info">
                            🕒 {gig.timeRange}
                          </span>
                        </td>
                        <td>
                          <span className="fw-semibold text-dark">
                            {gig.hours} Hours
                          </span>
                        </td>
                        <td>
                          <span className="badge badge-success px-3 py-2 fs-6">
                            ₹{gig.estPayoutMin} - ₹{gig.estPayoutMax}
                          </span>
                        </td>
                        <td className="text-end">
                          <button
                            className="btn btn-icon btn-round me-2"
                            title="Edit Gig"
                            onClick={() => handleOpenEditModal(gig)}
                            style={{
                              backgroundColor: "#FF6D1F",
                              borderColor: "#FF6D1F",
                              color: "#fff",
                            }}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="btn btn-icon btn-round btn-danger"
                            title="Delete Gig"
                            onClick={() => handleDeleteGig(gig)}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Modal Dialog for Add / Edit Gig */}
        {showModal && (
          <div
            className="modal fade show"
            tabIndex="-1"
            style={{
              display: "block",
              backgroundColor: "rgba(15, 23, 42, 0.6)",
            }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg border-0 rounded-4">
                <div className="modal-header border-bottom">
                  <h5 className="modal-title fw-bold text-dark">
                    {editingGig
                      ? "✏️ Edit Delivery Shift Gig"
                      : "⚡ Create New Shift Gig"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseModal}
                    disabled={saving}
                  ></button>
                </div>
                <form onSubmit={handleSaveGig}>
                  <div className="modal-body p-4">
                    {/* Gig Code */}
                    <div className="mb-3">
                      <label className="form-label fw-bold text-dark">
                        Gig Code (Unique Identifier)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.gigCode}
                        onChange={(e) =>
                          setFormData({ ...formData, gigCode: e.target.value })
                        }
                        placeholder="e.g. gig-1, gig-lunch-peak"
                        disabled={!!editingGig}
                      />
                    </div>

                    {/* Title */}
                    <div className="mb-3">
                      <label className="form-label fw-bold text-dark">
                        Shift Title <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.title}
                        onChange={(e) =>
                          setFormData({ ...formData, title: e.target.value })
                        }
                        placeholder="e.g. Evening Shift (Peak Demand)"
                        required
                      />
                    </div>

                    {/* Shift Start & End Time Pickers */}
                    <div className="row mb-3">
                      <div className="col-6">
                        <label className="form-label fw-bold text-dark">
                          Shift Start Time (From) <span className="text-danger">*</span>
                        </label>
                        <input
                          type="time"
                          className="form-control"
                          value={formData.timeFrom}
                          onChange={(e) => handleTimeChange("timeFrom", e.target.value)}
                        />
                      </div>
                      <div className="col-6">
                        <label className="form-label fw-bold text-dark">
                          Shift End Time (To) <span className="text-danger">*</span>
                        </label>
                        <input
                          type="time"
                          className="form-control"
                          value={formData.timeTo}
                          onChange={(e) => handleTimeChange("timeTo", e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Formatted Shift Time Slot Display / Custom Text Input */}
                    <div className="mb-3">
                      <label className="form-label fw-bold text-dark">
                        Formatted Shift Time Slot <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.timeRange}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            timeRange: e.target.value,
                          })
                        }
                        placeholder="e.g. 03:00 PM - 07:00 PM"
                        required
                      />
                      <small className="form-text text-muted">
                        Auto-generated from Start & End time (e.g. 03:00 PM - 07:00 PM). You can also edit it manually.
                      </small>
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-bold text-dark">
                        Shift Duration (Hours)
                      </label>
                      <input
                        type="number"
                        step="0.25"
                        className="form-control"
                        value={formData.hours}
                        onChange={(e) =>
                          setFormData({ ...formData, hours: e.target.value })
                        }
                        placeholder="e.g. 4.0"
                      />
                    </div>

                    {/* Payout Min & Max Row */}
                    <div className="row">
                      <div className="col-6 mb-3">
                        <label className="form-label fw-bold text-dark">
                          Min Payout (₹)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={formData.estPayoutMin}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              estPayoutMin: e.target.value,
                            })
                          }
                          placeholder="300"
                        />
                      </div>
                      <div className="col-6 mb-3">
                        <label className="form-label fw-bold text-dark">
                          Max Payout (₹)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={formData.estPayoutMax}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              estPayoutMax: e.target.value,
                            })
                          }
                          placeholder="500"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer border-top bg-light">
                    <button
                      type="button"
                      className="btn btn-secondary btn-round"
                      onClick={handleCloseModal}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary btn-round"
                      disabled={saving}
                      style={{
                        backgroundColor: "#FF6D1F",
                        borderColor: "#FF6D1F",
                        fontWeight: "600",
                      }}
                    >
                      {saving
                        ? "Saving..."
                        : editingGig
                          ? "Update Shift Gig"
                          : "Create Shift Gig"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryGigs;
